import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * Select Component
 * Un componente select personalizzato che supporta selezione singola e multipla
 * con pieno supporto per temi light/dark.
 * 
 * @component
 * @example
 * // Select base
 * <Select
 *   options={[
 *     { value: '1', label: 'Opzione 1' },
 *     { value: '2', label: 'Opzione 2' }
 *   ]}
 *   value={selectedValue}
 *   onChange={(value) => setSelectedValue(value)}
 * />
 * 
 * @example
 * // Select multiplo
 * <Select
 *   multiple
 *   options={[
 *     { value: '1', label: 'Opzione 1' },
 *     { value: '2', label: 'Opzione 2' }
 *   ]}
 *   value={selectedValues}
 *   onChange={(values) => setSelectedValues(values)}
 * />
 * 
 * @param {Object} props - Proprietà del componente
 * @param {Array<{value: string, label: string}>} props.options - Array di opzioni da mostrare
 * @param {string|string[]} [props.value] - Valore/i selezionato/i
 * @param {Function} [props.onChange] - Callback chiamata quando cambia la selezione
 * @param {string} [props.placeholder='Seleziona...'] - Testo mostrato quando nessun valore è selezionato
 * @param {boolean} [props.disabled=false] - Disabilita il select
 * @param {boolean} [props.error=false] - Mostra lo stato di errore
 * @param {string} [props.className=''] - Classi CSS aggiuntive
 * @param {boolean} [props.multiple=false] - Abilita la selezione multipla
 * 
 * @returns {JSX.Element} Componente Select renderizzato
 */
const Select = ({
  options = [],
  value,
  onChange,
  placeholder = 'Seleziona...',
  disabled = false,
  error = false,
  className = '',
  multiple = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [selectedValues, setSelectedValues] = useState(multiple ? [] : null);
  const selectRef = useRef(null);

  useEffect(() => {
    if (value) {
      setSelectedValues(value);
    }
  }, [value]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      setIsOpen(false);
    }, 200); // Durata dell'animazione
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    let newValue;
    if (multiple) {
      newValue = selectedValues.includes(option.value)
        ? selectedValues.filter(v => v !== option.value)
        : [...selectedValues, option.value];
    } else {
      newValue = option.value;
      handleClose();
    }
    
    setSelectedValues(newValue);
    onChange && onChange(newValue);
  };

  const getDisplayValue = () => {
    if (!selectedValues || (Array.isArray(selectedValues) && selectedValues.length === 0)) {
      return placeholder;
    }

    if (multiple) {
      return selectedValues
        .map(val => options.find(opt => opt.value === val)?.label)
        .join(', ');
    }

    return options.find(opt => opt.value === selectedValues)?.label;
  };

  const baseClasses = `
    relative w-full min-w-[200px] px-3 py-2 rounded-lg
    transition-colors duration-200
    border border-transparent
    ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
    ${error ? 'border-light-error dark:border-dark-error' : ''}
    bg-light-surface dark:bg-dark-surface
    text-light-on-surface dark:text-dark-on-surface
    hover:border-light-primary dark:hover:border-dark-primary
    hover:bg-light-primary/5 dark:hover:bg-dark-primary/5
    focus:border-light-primary dark:focus:border-dark-primary
    focus:bg-light-primary/10 dark:focus:bg-dark-primary/10
  `;

  const dropdownClasses = `
    absolute top-full left-0 w-full mt-1 
    bg-light-surface dark:bg-dark-surface
    border border-light-outline dark:border-dark-outline
    rounded-lg shadow-surface-container
    z-50 max-h-60 overflow-y-auto
    transition-all duration-200 ease-in-out
    ${isOpen && !isClosing ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'}
  `;

  return (
    <div ref={selectRef} className={`${baseClasses} ${className}`}>
      <div 
        className="flex items-center justify-between"
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className={!selectedValues ? 'text-light-on-surface-variant dark:text-dark-on-surface-variant' : ''}>
          {getDisplayValue()}
        </span>
        <ChevronDown 
          size={20}
          className={`
            transition-transform duration-300 
            text-light-on-surface-variant dark:text-dark-on-surface-variant
            ${isOpen ? 'rotate-180' : ''}
          `}
        />
      </div>

      <div className={dropdownClasses}>
        {options.map((option) => (
          <div
            key={option.value}
            className={`
              px-4 py-2 cursor-pointer
              transition-colors duration-200
              ${multiple && selectedValues.includes(option.value) 
                ? 'bg-light-primary dark:bg-dark-primary text-light-on-primary dark:text-dark-on-primary' 
                : `
                  text-light-on-surface dark:text-dark-on-surface
                  hover:bg-light-primary/10 dark:hover:bg-dark-primary/10
                  hover:text-light-on-primary dark:hover:text-dark-on-primary
                `
              }
            `}
            onClick={() => handleSelect(option)}
          >
            {option.label}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Select;
